import { ReactElement } from 'react';

import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { translate } from '@cloud-wave/neon-common-lib/app';

import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Icon from '../Icon';
import Tooltip from '../atoms/Tooltip';

import styles from './segmented-controls.module.scss';

type TStyleType = 'JOINED' | 'SEPARATED';

export type TSegmentedControlsOption = {
  icon?: IconDefinition | ReactElement;
  disabled?: boolean;
  tooltip?: string;
} & ({ label: string; value?: string } | { label?: string; value: string });
/* this construct specifies that at least one of these properties is required,
 * but both may be provided. if this is a common enough pattern elsewhere this
 * should be extracted to a common utility type.
 */

export type TControlledSegmentedControls = {
  options: TSegmentedControlsOption[];
  onSelect?: ((option: string) => void) | ((option: string[]) => void);
  className?: string;
  square?: boolean;
  inverse?: boolean;
  small?: boolean;
  multi?: boolean;
  multiMinimum?: number;
  selectedIndices?: number | number[];
  setSelectedIndices?: (indices: number[]) => void;
  ariaLabel?: string;
  disabled?: boolean;
  styleType?: TStyleType;
};

function SegmentedControlsOption({
  label,
  icon,
  value,
  selected,
  onClick,
  multi,
  small,
  disabled = false,
  tooltip,
  styleType,
  atMinimumSelections,
  square,
}: TSegmentedControlsOption & {
  selected: boolean;
  onClick?: (option: any) => void;
  multi: boolean;
  small: boolean;
  styleType: TStyleType;
  atMinimumSelections?: boolean;
  square?: boolean;
}) {
  const organisationSpelling = translate('Organisation');
  // eslint-disable-next-line i18next/no-literal-string -- search term for the untranslated label, not display text
  const translatedLabel = label?.replace('Organisation', organisationSpelling);

  return (
    <Tooltip ariaHidden title={tooltip ?? ''} placement="top" enterDelay={1000}>
      <button
        aria-label={label || value}
        role="tab"
        aria-selected={selected}
        className={cx(styles['segmented-controls__option'], {
          [styles['segmented-controls__option--selected']]: selected,
          [styles['segmented-controls__option--small']]: small,
          [styles['segmented-controls__option--disabled']]: disabled,
          [styles['segmented-controls__option--icon-only']]: !label && icon,
          [styles['segmented-controls__option--joined']]: styleType === 'JOINED',
          [styles['segmented-controls__option--square']]: square,
          [styles['segmented-controls__option--clickable']]:
            (multi && selected && !atMinimumSelections) || (!selected && !disabled),
        })}
        onClick={disabled || (selected && !multi) || (selected && atMinimumSelections) ? void 0 : onClick}
        data-testid={`segmented-control-option-${typeof label === 'string' ? label.toLowerCase() : value}`}
      >
        {icon && 'iconName' in icon ? (
          <Icon className={cx({ 'mr-5': label })} icon={icon} size={small ? 15 : 17} />
        ) : (
          icon
        )}
        {translatedLabel}
      </button>
    </Tooltip>
  );
}

export default function ControlledSegmentedControls({
  options,
  onSelect,
  className,
  square,
  inverse,
  small = false,
  multi = false,
  multiMinimum = 0,
  selectedIndices = [],
  setSelectedIndices,
  ariaLabel,
  disabled,
  styleType = 'SEPARATED',
}: TControlledSegmentedControls) {
  const { isSoftphone } = useLayout();
  const selectedIndicesArray = typeof selectedIndices === 'number' ? [selectedIndices] : selectedIndices;
  const atMinimumSelections =
    Boolean(multi && selectedIndicesArray.length <= multiMinimum) || Boolean(!multi && selectedIndicesArray.length);

  const getKeyFromOption = (index: number) => (options[index].value ?? options[index].label) as string;

  const handleSingleSelect = (index: number) => () => {
    setSelectedIndices?.([index]);

    //@ts-expect-error typing is tricky here
    return onSelect?.(getKeyFromOption(index));
  };

  const multiUnselectItem = (index: number) => {
    const newState = [...selectedIndicesArray, index];
    //@ts-expect-error typing is tricky here
    onSelect?.(newState.map(getKeyFromOption));
    setSelectedIndices?.(newState);
  };

  const multiSelectItem = (index: number) => {
    const newState = selectedIndicesArray.filter((i: number) => i !== index);
    //@ts-expect-error typing is tricky here
    onSelect?.(newState.map(getKeyFromOption));
    setSelectedIndices?.(newState);
  };

  const handleMultiSelect = (index: number) => () => {
    const itemSelected = selectedIndicesArray.includes(index);

    if (!itemSelected) {
      return multiUnselectItem(index);
    }

    if (!atMinimumSelections) {
      return multiSelectItem(index);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(
        styles['segmented-controls'],
        {
          [styles['segmented-controls--square']]: square && styleType !== 'JOINED',
          [styles['segmented-controls--inverse']]: inverse,
          [styles['segmented-controls--multi']]: multi,
          [styles['segmented-controls--softphone']]: isSoftphone,
          [styles['segmented-controls--joined']]: styleType === 'JOINED',
        },
        className
      )}
    >
      {options.map((option, index) => (
        <SegmentedControlsOption
          key={option.value ?? option.label}
          selected={selectedIndicesArray.includes(index)}
          onClick={multi ? handleMultiSelect(index) : handleSingleSelect(index)}
          multi={multi}
          small={small}
          styleType={styleType}
          atMinimumSelections={atMinimumSelections}
          square={square}
          {...option}
          disabled={disabled || option.disabled}
        />
      ))}
    </div>
  );
}
